import React, { useState, useEffect, useRef } from 'react';

const AromaPodProApp = () => {
  // State management
  const [activeElement, setActiveElement] = useState(null);
  const [activeIntensity, setActiveIntensity] = useState(null);
  const [isPoweredOn, setIsPoweredOn] = useState(true);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [weatherState, setWeatherState] = useState(0); // 0 = Partly Cloudy, 1 = Sunny, 2 = Rainy, 3 = Snowy
  const [isCloudQrModalOpen, setIsCloudQrModalOpen] = useState(false);
  
  const nightModeTimerRef = useRef(null);
  
  // Weather states data
  const weatherStates = [
    {
      condition: "Partly Cloudy",
      temp: "56°F",
      icon: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M19.36 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.64-4.96zM19 18H6c-2.21 0-4-1.79-4-4 0-2.05 1.53-3.76 3.56-3.97l1.07-.11.5-.95C8.08 7.14 9.94 6 12 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5 1.53.11c1.56.1 2.78 1.41 2.78 2.96 0 1.65-1.35 3-3 3zm-8.5-5.5h3l-4.5 6.5v-4h-3l4.5-6.5v4z'/%3E%3C/svg%3E\")"
    },
    {
      condition: "Sunny",
      temp: "75°F",
      icon: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zm7.45 3.91l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79zm-3.21 13.7l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-3.91l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z'/%3E%3C/svg%3E\")"
    },
    {
      condition: "Rainy",
      temp: "48°F",
      icon: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M17.66 7.93L12 2.27 6.34 7.93c-3.12 3.12-3.12 8.19 0 11.31C7.9 20.8 9.95 21.58 12 21.58c2.05 0 4.1-.78 5.66-2.34 3.12-3.12 3.12-8.19 0-11.31zM12 19.59c-1.6 0-3.11-.62-4.24-1.76C6.62 16.69 6 15.19 6 13.59s.62-3.11 1.76-4.24L12 5.1v14.49z'/%3E%3C/svg%3E\")"
    },
    {
      condition: "Snowy",
      temp: "28°F",
      icon: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z'/%3E%3C/svg%3E\")"
    }
  ];
  
  // Element messages
  const elementMessages = {
    'Metal': 'Metal essence promotes focus and clarity',
    'Wood': 'Wood essence brings harmony and growth',
    'Fire': 'Fire essence enhances energy and passion',
    'Earth': 'Earth essence grounds and balances'
  };
  
  // Update time display
  useEffect(() => {
    const updateTimeDisplay = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      setCurrentTime(`${hours}:${minutes} ${ampm}`);
    };
    
    // Initial time update
    updateTimeDisplay();
    
    // Update time every minute
    const intervalId = setInterval(updateTimeDisplay, 60000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Night mode timer
  useEffect(() => {
    startNightModeTimer();
    
    return () => {
      clearTimeout(nightModeTimerRef.current);
    };
  }, []);
  
  const startNightModeTimer = () => {
    nightModeTimerRef.current = setTimeout(() => {
      setIsPoweredOn(false);
    }, 600000); // 10 minutes
  };
  
  const resetNightModeTimer = () => {
    clearTimeout(nightModeTimerRef.current);
    startNightModeTimer();
  };
  
  // Handle element selection
  const handleElementSelect = (element) => {
    resetNightModeTimer();
    
    if (activeElement === element) {
      setActiveElement(null);
      return;
    }
    
    setActiveElement(element);
    
    // If no intensity selected, default to medium (3)
    if (!activeIntensity) {
      setActiveIntensity(3);
    }
  };
  
  // Handle intensity selection
  const handleIntensitySelect = (intensity) => {
    resetNightModeTimer();
    
    // If no element selected, select the default (Metal)
    if (!activeElement) {
      setActiveElement('Metal');
    }
    
    setActiveIntensity(intensity);
  };
  
  // Handle power button
  const handlePowerToggle = () => {
    if (isPoweredOn) {
      // Enter night mode
      setIsPoweredOn(false);
      clearTimeout(nightModeTimerRef.current);
    } else {
      // Exit night mode
      setIsPoweredOn(true);
      resetNightModeTimer();
    }
  };
  
  // Wake from night mode
  const handleWakeUp = () => {
    setIsPoweredOn(true);
    resetNightModeTimer();
  };
  
  // CSS styles
  const styles = {
    deviceContainer: {
      position: 'relative',
      width: '440px',
      height: '280px',
      backgroundColor: '#000',
      overflow: 'hidden',
      borderRadius: '10px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      border: '6px solid #333',
      fontFamily: "'Helvetica Neue', Arial, sans-serif",
      userSelect: 'none',
    },
    
    screen: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
      color: '#fff',
      transition: 'transform 0.5s ease, opacity 0.5s ease',
    },
    
    header: {
      height: '30px',
      padding: '8px 15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'rgba(0,0,0,0.2)',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
    
    weatherInfo: {
      display: 'flex',
      alignItems: 'center',
    },
    
    weatherIcon: {
      width: '35px',
      height: '35px',
      marginRight: '10px',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundImage: weatherStates[weatherState].icon,
    },
    
    weatherText: {
      fontSize: '13px',
      color: 'rgba(255,255,255,0.9)',
    },
    
    weatherTemp: {
      fontSize: '15px',
      fontWeight: 'bold',
      marginLeft: '5px',
    },
    
    time: {
      fontSize: '20px',
      fontWeight: 300,
      color: '#fff',
    },
    
    mainContent: {
      height: '185px',
      padding: '5px 15px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.1)',
    },
    
    welcomeMessage: {
      fontSize: '18px',
      fontWeight: 500,
      marginBottom: '6px',
      color: '#fff',
      textAlign: 'center',
    },
    
    message: {
      textAlign: 'center',
      fontSize: '14px',
      color: 'rgba(255,255,255,0.8)',
      marginBottom: '2px',
      fontWeight: 300,
      lineHeight: 1.4,
    },
    
    elementsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '12px',
      width: '100%',
      maxWidth: '400px',
      marginTop: '5px',
    },
    
    elementBtn: (type, isActive) => ({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '8px 5px',
      background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.15)',
      cursor: 'pointer',
      height: '65px',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      transform: isActive ? 'translateY(-3px)' : 'translateY(0)',
      boxShadow: isActive ? '0 5px 15px rgba(0,0,0,0.2)' : 'none',
    }),
    
    elementBefore: (type) => ({
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundSize: '60%',
      backgroundPosition: 'center 35%',
      backgroundRepeat: 'no-repeat',
      opacity: 0.8,
      transition: 'all 0.3s ease',
      backgroundImage: 
        type === 'Metal' ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3zm5 15h-2v-6H9v6H7v-7.81l5-4.5 5 4.5V18z'/%3E%3C/svg%3E\")" :
        type === 'Wood' ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M12.74 2.32a1 1 0 00-1.48 0l-9 10A1 1 0 003 14h2v7a1 1 0 001 1h12a1 1 0 001-1v-7h2a1 1 0 00.78-1.63l-9-10zM9 20v-6h6v6H9z'/%3E%3C/svg%3E\")" :
        type === 'Fire' ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M17.66 11.2c-.23-.3-.51-.56-.77-.82-.67-.6-1.43-1.03-2.07-1.66C13.33 7.26 13 4.85 13.95 3c-.95.23-1.78.75-2.49 1.32-2.59 2.08-3.61 5.75-2.39 8.9.04.1.08.2.08.33 0 .22-.15.42-.35.5-.23.1-.47.04-.66-.12a.58.58 0 01-.14-.17c-1.13-1.43-1.31-3.48-.55-5.12C5.78 10 4.87 12.3 5 14.47c.06.5.12 1 .29 1.5.14.6.41 1.2.71 1.73 1.08 1.73 2.95 2.97 4.96 3.22 2.14.27 4.43-.12 6.07-1.6 1.83-1.66 2.47-4.32 1.53-6.6l-.13-.26c-.21-.46-.77-1.26-.77-1.26m-3.16 6.3c-.28.24-.74.5-1.1.6-1.12.4-2.24-.16-2.9-.82 1.19-.28 1.9-1.16 2.11-2.05.17-.8-.15-1.46-.28-2.23-.12-.74-.1-1.37.17-2.06.19.38.39.76.63 1.06.77 1 1.98 1.44 2.24 2.8.04.14.06.28.06.43.03.82-.33 1.72-.93 2.27z'/%3E%3C/svg%3E\")" :
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z'/%3E%3C/svg%3E\")"
    }),
    
    elementActiveIndicator: {
      content: '""',
      position: 'absolute',
      top: '5px',
      right: '5px',
      width: '8px',
      height: '8px',
      backgroundColor: '#4ade80',
      borderRadius: '50%',
    },
    
    elementName: {
      fontSize: '11px',
      textAlign: 'center',
      color: '#fff',
      position: 'relative',
      zIndex: 1,
    },
    
    intensitySection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '12px',
      width: '100%',
    },
    
    intensityLabel: {
      fontSize: '13px',
      marginBottom: '6px',
      color: 'rgba(255,255,255,0.8)',
    },
    
    intensityControls: {
      display: 'flex',
      alignItems: 'center',
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '25px',
      padding: '4px',
      width: '90%',
      maxWidth: '280px',
    },
    
    intensityBtn: (isActive) => ({
      flex: 1,
      textAlign: 'center',
      padding: '6px 0',
      borderRadius: '18px',
      color: '#fff',
      fontSize: '11px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: isActive ? 'rgba(255,255,255,0.3)' : 'transparent',
      boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
    }),
    
    footer: {
      height: '40px',
      padding: '0 15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'rgba(0,0,0,0.3)',
      borderTop: '1px solid rgba(255,255,255,0.1)',
    },
    
    qrCode: {
      display: 'flex',
      alignItems: 'center',
      color: 'rgba(255,255,255,0.8)',
      fontSize: '11px',
      cursor: 'pointer',
      padding: '5px 10px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '15px',
      transition: 'all 0.2s ease',
    },
    
    qrIcon: {
      width: '16px',
      height: '16px',
      marginRight: '5px',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2v-2zm0 4h2v2h-2v-2zm4 0h2v2h-2v-2zm0-4h2v2h-2v-2zm-8 0h2v2H9v-2zm4 4h2v2h-2v-2zm4-8h2v2h-2V9zm0 4h2v2h-2v-2zm-8 4h2v2H9v-2z'/%3E%3C/svg%3E\")",
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    },
    
    powerBtn: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '1px solid rgba(255,255,255,0.2)',
    },
    
    powerIcon: {
      width: '16px',
      height: '16px',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z'/%3E%3C/svg%3E\")",
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    },
    
    qrModal: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100,
      opacity: isQrModalOpen ? 1 : 0,
      pointerEvents: isQrModalOpen ? 'all' : 'none',
      transition: 'opacity 0.3s ease',
    },
    
    qrModalContent: {
      background: 'white',
      padding: '15px',
      borderRadius: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
    
    qrImage: {
      width: '130px',
      height: '130px',
      backgroundColor: '#fff',
      marginBottom: '10px',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 33 33' shape-rendering='crispEdges'%3E%3Cpath fill='%23ffffff' d='M0 0h33v33H0z'/%3E%3Cpath stroke='%23000000' d='M4 4.5h7m1 0h1m1 0h2m2 0h1m1 0h1m1 0h7M4 5.5h1m5 0h1m1 0h3m3 0h3m1 0h1m5 0h1M4 6.5h1m1 0h3m1 0h1m2 0h3m1 0h1m1 0h1m1 0h3m1 0h1m1 0h3m1 0h1M4 7.5h1m1 0h3m1 0h1m1 0h1m1 0h1m3 0h1m3 0h1m1 0h1m1 0h3m1 0h1M4 8.5h1m1 0h3m1 0h1m1 0h1m1 0h5m3 0h1m1 0h1m1 0h3m1 0h1M4 9.5h1m5 0h1m1 0h4m1 0h2m2 0h1m1 0h1m5 0h1M4 10.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M12 11.5h1m1 0h3m1 0h2M5 12.5h1m1 0h2m1 0h5m1 0h4m2 0h1m1 0h2m3 0h1M4 13.5h1m1 0h1m3 0h1m4 0h1m1 0h1m1 0h1m1 0h1m1 0h1m4 0h3M4 14.5h1m1 0h5m1 0h1m2 0h2m2 0h4m4 0h1m1 0h2M4 15.5h2m4 0h1m2 0h1m1 0h1m3 0h2m2 0h1m3 0h1m2 0h1M9 16.5h1m3 0h1m1 0h2m1 0h1m2 0h1m1 0h1m5 0h1M4 17.5h7m1 0h1m1 0h1m2 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m2 0h1M4 18.5h1m5 0h1m3 0h1m2 0h2m5 0h3m1 0h1M4 19.5h1m1 0h3m1 0h1m6 0h2m2 0h1m2 0h2m1 0h1m1 0h1M4 20.5h1m1 0h3m1 0h1m1 0h3m3 0h1m2 0h1m1 0h1m1 0h1m1 0h3M4 21.5h1m1 0h3m1 0h1m2 0h1m1 0h2m1 0h2m1 0h1m1 0h4m1 0h1M4 22.5h1m5 0h1m1 0h1m3 0h1m2 0h1m1 0h3m1 0h3M4 23.5h7m1 0h1m3 0h4m1 0h1m1 0h1m3 0h1m1 0h1M12 24.5h3m1 0h1m1 0h4m3 0h1m2 0h1M5 25.5h6m1 0h1m1 0h1m1 0h1m2 0h1m1 0h1m1 0h3m2 0h1M4 26.5h2m2 0h3m2 0h3m5 0h1m2 0h2m1 0h2M5 27.5h1m1 0h1m3 0h1m3 0h1m4 0h1m3 0h2m1 0h1M4 28.5h1m2 0h1m1 0h1m1 0h4m1 0h1m1 0h1m3 0h1m2 0h3M12 29.5h1m1 0h1m1 0h1m5 0h2m4 0h1M4 30.5h7m1 0h3m1 0h1m4 0h1m1 0h1m3 0h1M4 31.5h1m5 0h1m1 0h1m4 0h3m2 0h1m1 0h1m2 0h1M4 32.5h1m1 0h3m1 0h1m2 0h1m1 0h1m1 0h1m1 0h1m1 0h1m2 0h1m1 0h4'/%3E%3C/svg%3E\")",
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    
    qrText: {
      color: '#000',
      fontSize: '13px',
      marginBottom: '12px',
      maxWidth: '180px',
    },
    
    closeQr: {
      marginTop: '8px',
      padding: '6px 12px',
      background: '#0f172a',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '11px',
    },
    
    nightModeScreen: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 90,
      opacity: isPoweredOn ? 0 : 1,
      pointerEvents: isPoweredOn ? 'none' : 'all',
      transition: 'opacity 0.5s ease',
    },
    
    nightModeText: {
      color: 'rgba(255,255,255,0.3)',
      fontSize: '14px',
    },

    // 在styles對象中添加新的樣式
cloudControlBtn: {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.8)',
    fontSize: '11px',
    cursor: 'pointer',
    padding: '5px 10px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '15px',
    transition: 'all 0.2s ease',
  },
  
  cloudIcon: {
    width: '16px',
    height: '16px',
    marginRight: '5px',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4s1.79-4 4-4h.71C7.37 7.69 9.48 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3s-1.34 3-3 3z'/%3E%3C/svg%3E\")",
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  };
  
  return (
    <div style={styles.deviceContainer}>
      {/* Main Screen */}
      <div style={styles.screen}>
        {/* Header with weather and time */}
        <div style={styles.header}>
          <div style={styles.weatherInfo}>
            <div style={styles.weatherIcon}></div>
            <div style={styles.weatherText}>
              <span>{weatherStates[weatherState].condition}</span>
              <span style={styles.weatherTemp}>{weatherStates[weatherState].temp}</span>
            </div>
          </div>
          <div style={styles.time}>{currentTime}</div>
        </div>
        
        {/* Main content area */}
        <div style={styles.mainContent}>
          <div style={styles.welcomeMessage}>Luxury Aromatherapy</div>
          <div style={styles.message}>
            {activeElement 
              ? elementMessages[activeElement]
              : 'Select an element to enhance your wellness experience'
            }
          </div>
          
          {/* Elements selection grid */}
          <div style={styles.elementsGrid}>
            {['Metal', 'Wood', 'Fire', 'Earth'].map(element => (
              <div 
                key={element}
                style={styles.elementBtn(element, activeElement === element)} 
                onClick={() => handleElementSelect(element)}
              >
                <div style={styles.elementBefore(element)}></div>
                {activeElement === element && <div style={styles.elementActiveIndicator}></div>}
                <span style={styles.elementName}>{element}</span>
              </div>
            ))}
          </div>
          
          {/* Intensity control */}
          <div style={styles.intensitySection}>
            {/* <div style={styles.intensityLabel}>Intensity</div> */}
            <div style={styles.intensityControls}>
              {['Gentle', 'Soft', 'Medium', 'Strong', 'Intense'].map((label, index) => (
                <div 
                  key={label}
                  style={styles.intensityBtn(activeIntensity === index + 1)}
                  onClick={() => handleIntensitySelect(index + 1)}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer with QR codes and power */}
        <div style={styles.footer}>
          <div 
            style={styles.qrCode}
            onClick={() => {
              setIsQrModalOpen(true);
              resetNightModeTimer();
            }}
          >
            <div style={styles.qrIcon}></div>
            <span>Take Home</span>
          </div>
          
          <div 
            style={styles.cloudControlBtn}
            onClick={() => {
              setIsCloudQrModalOpen(true);
              resetNightModeTimer();
            }}
          >
            <div style={styles.cloudIcon}></div>
            <span>Cloud Control</span>
          </div>
          
          <div 
            style={styles.powerBtn}
            onClick={handlePowerToggle}
          >
            <div style={styles.powerIcon}></div>
          </div>
        </div>
      </div>
      
      {/* Take Home QR Modal */}
      <div style={{
        ...styles.qrModal,
        opacity: isQrModalOpen ? 1 : 0,
        pointerEvents: isQrModalOpen ? 'all' : 'none',
      }}>
        <div style={styles.qrModalContent}>
          <div style={styles.qrImage}></div>
          <div style={styles.qrText}>
            Scan to experience AromaPod Pro on your device or purchase for home
          </div>
          <button 
            style={styles.closeQr}
            onClick={() => setIsQrModalOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Cloud Control QR Modal */}
      <div style={{
        ...styles.qrModal,
        opacity: isCloudQrModalOpen ? 1 : 0,
        pointerEvents: isCloudQrModalOpen ? 'all' : 'none',
      }}>
        <div style={styles.qrModalContent}>
          <div style={{
            ...styles.qrImage,
            backgroundImage: "url(\"data:image/png;base64,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\")",
          }}></div>
          <div style={styles.qrText}>
            Scan to control your AromaPod Pro remotely through the hotel's exclusive app
          </div>
          <button 
            style={styles.closeQr}
            onClick={() => setIsCloudQrModalOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Night Mode Screen */}
      <div 
        style={styles.nightModeScreen}
        onClick={handleWakeUp}
      >
        <div style={styles.nightModeText}>Touch to wake</div>
      </div>
    </div>
  );
}

export default AromaPodProApp;